import { useState, useEffect, useCallback } from 'react';
import * as service from './whatsappConfigService';
import { useWebSocket } from '../../websocket/WebSocketContext';

const EMPTY_FORM = {
    _id: '',
    appName: '',
    whatsappUrl: '',
    username: '',
    whatsapp_vendor_name: '',
    whatsapp_config_type: '',
    notification_email: '',
};

export const QUEUE_TYPES = [
    { value: '0', label: 'Local Queue', color: 'blue' },
    { value: '1', label: 'Kafka Queue', color: 'green' },
    { value: '2', label: 'Redis Queue', color: 'red' },
];

export default function useWhatsappConfig({ API_BASE_URL, loginId, deliveryMode }) {
    const { client } = useWebSocket();
    const ctx = { deliveryMode, client, loginId };

    // ── Data ────────────────────────────────────────────────────────────────
    const [configs, setConfigs] = useState([]);
    const [appNameStatuses, setAppNameStatuses] = useState({});
    const [currentQueueType, setCurrentQueueType] = useState(null);

    // ── UI / Loading ────────────────────────────────────────────────────────
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingQueue, setIsLoadingQueue] = useState(false);
    const [isLoadingAppStatuses, setIsLoadingAppStatuses] = useState(false);
    const [isUpdatingQueue, setIsUpdatingQueue] = useState(false);
    const [isUpdatingAppStatus, setIsUpdatingAppStatus] = useState(false);
    const [updatingAppName, setUpdatingAppName] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    // ── Messages ────────────────────────────────────────────────────────────
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [queueSuccess, setQueueSuccess] = useState('');
    const [queueError, setQueueError] = useState('');

    // ── Form / Edit ─────────────────────────────────────────────────────────
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);

    // ── Modal ───────────────────────────────────────────────────────────────
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [configToUpdate, setConfigToUpdate] = useState(null);
    const [licenseNo, setLicenseNo] = useState('');
    const [licenseError, setLicenseError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // ── Selected Queue ──────────────────────────────────────────────────────
    const [selectedQueueType, setSelectedQueueType] = useState('0');

    // Auto-clear messages
    useEffect(() => {
        if (!success && !error) return;
        const t = setTimeout(() => { setSuccess(''); setError(''); }, 4000);
        return () => clearTimeout(t);
    }, [success, error]);

    useEffect(() => {
        if (!queueSuccess && !queueError) return;
        const t = setTimeout(() => { setQueueSuccess(''); setQueueError(''); }, 4000);
        return () => clearTimeout(t);
    }, [queueSuccess, queueError]);

    // ── Service Callers ─────────────────────────────────────────────────────

    const loadConfigs = useCallback(async () => {
        setIsLoading(true);
        try {
            const list = await service.fetchAllConfigs(ctx);
            setConfigs(list);
            if (list.length > 0 && !selectedConfig) setSelectedConfig(list[0]);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [API_BASE_URL]);

    const loadQueueType = useCallback(async () => {
        setIsLoadingQueue(true);
        try {
            const qt = await service.fetchQueueType(ctx);
            setCurrentQueueType(qt);
        } catch {
            /* silent */
        } finally {
            setIsLoadingQueue(false);
        }
    }, [API_BASE_URL]);

    const loadAppStatuses = useCallback(async () => {
        setIsLoadingAppStatuses(true);
        try {
            const statuses = await service.fetchAppNameStatuses(ctx);
            setAppNameStatuses(statuses);
        } catch (err) {
            setQueueError(err.message);
        } finally {
            setIsLoadingAppStatuses(false);
        }
    }, [API_BASE_URL]);

    const handleRefresh = () => {
        loadConfigs();
        loadQueueType();
        loadAppStatuses();
    };

    useEffect(() => {
        handleRefresh();
    }, [API_BASE_URL]);

    // ── Form Helpers ────────────────────────────────────────────────────────

    const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

    const resetForm = () => {
        setFormData(EMPTY_FORM);
        setIsEditing(false);
    };

    const loadConfigForEdit = (config) => {
        setFormData({
            _id: config._id,
            appName: config.appName || '',
            whatsappUrl: config.whatsappUrl || '',
            username: config.username || '',
            whatsapp_vendor_name: config.whatsapp_vendor_name || '',
            whatsapp_config_type: config.whatsapp_config_type || '',
            notification_email: config.notification_email || '',
        });

        setIsEditing(true);
    };
    // ── Save ────────────────────────────────────────────────────────────────

    const handleSaveConfig = async () => {
        const required = [
            'appName',
            'whatsappUrl',
            'username',
            'whatsapp_vendor_name',
            'whatsapp_config_type',
            'notification_email',
        ];

        for (const f of required) {
            if (!formData[f]?.trim()) {
                setError(`${f.replace(/_/g, ' ').toUpperCase()} is required.`);
                return;
            }
        }
        setIsSaving(true);
        try {
            console.log("formData = ", formData)
            await service.saveConfig({ ...ctx, formData, isEditing, configId: selectedConfig?._id });
            setSuccess(`WhatsApp configuration ${isEditing ? 'updated' : 'saved'} successfully!`);
            resetForm();
            await loadConfigs();
            await loadQueueType();
            await loadAppStatuses();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    // ── Toggle Config Status ────────────────────────────────────────────────

    const handleToggleClick = (config) => {
        setConfigToUpdate(config);
        setIsLicenseModalOpen(true);
    };

    const handleLicenseSubmit = () => {
        if (!licenseNo.trim()) { setLicenseError('License number cannot be empty.'); return; }
        setLicenseError('');
        setIsLicenseModalOpen(false);
        setIsConfirmModalOpen(true);
    };

    const executeStatusUpdate = async () => {
        if (!configToUpdate || !licenseNo) return;
        setIsConfirmModalOpen(false);
        setIsUpdating(true);
        try {
            const data = await service.toggleConfigStatus({ ...ctx, configId: configToUpdate._id, currentStatus: configToUpdate.STATUS, licenseNumber: licenseNo });
            const newStatus = data.status;
            setSuccess(`WhatsApp configuration ${newStatus?.toLowerCase()}d successfully!`);
            setConfigs(prev => prev.map(c => c._id === configToUpdate._id ? { ...c, STATUS: newStatus } : c));
            if (selectedConfig?._id === configToUpdate._id) setSelectedConfig(prev => ({ ...prev, STATUS: newStatus }));
            await loadQueueType();
            await loadAppStatuses();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsUpdating(false);
            setConfigToUpdate(null);
            setLicenseNo('');
        }
    };

    // ── Queue ───────────────────────────────────────────────────────────────

    const handleUpdateQueueType = async () => {
        setIsUpdatingQueue(true);
        try {
            await service.updateQueueType({ ...ctx, queueType: selectedQueueType });
            const label = QUEUE_TYPES.find(q => q.value === selectedQueueType)?.label;
            setQueueSuccess(`Queue type switched to ${label} successfully!`);
            setCurrentQueueType(selectedQueueType);
        } catch (err) {
            setQueueError(err.message);
        } finally {
            setIsUpdatingQueue(false);
        }
    };

    // ── App Name Status ─────────────────────────────────────────────────────

    const handleToggleAppNameStatus = async (appName, isActive) => {
        setIsUpdatingAppStatus(true);
        setUpdatingAppName(appName);
        try {
            const { active } = await service.toggleAppNameStatus({ ...ctx, appName, currentStatus: isActive });
            const statusText = active === 'true' ? 'activated' : 'deactivated';
            setQueueSuccess(`App "${appName}" ${statusText} successfully!`);
            await loadAppStatuses();
        } catch (err) {
            setQueueError(err.message);
        } finally {
            setIsUpdatingAppStatus(false);
            setUpdatingAppName(null);
        }
    };

    return {
        // data
        configs, appNameStatuses, currentQueueType,
        // loading
        isLoading, isLoadingQueue, isLoadingAppStatuses,
        isUpdatingQueue, isUpdatingAppStatus, updatingAppName,
        isSaving, isUpdating,
        // messages
        success, error, queueSuccess, queueError,
        // form
        formData, isEditing, handleInputChange, resetForm, loadConfigForEdit, handleSaveConfig,
        // selected
        selectedConfig, setSelectedConfig,
        // modals
        isDetailsModalOpen, setIsDetailsModalOpen,
        isLicenseModalOpen, setIsLicenseModalOpen,
        isConfirmModalOpen, setIsConfirmModalOpen,
        configToUpdate, licenseNo, setLicenseNo, licenseError, showPassword, setShowPassword,
        // queue
        selectedQueueType, setSelectedQueueType, handleUpdateQueueType,
        // handlers
        handleRefresh, handleToggleClick, handleLicenseSubmit, executeStatusUpdate,
        handleToggleAppNameStatus,
        loadQueueType, loadAppStatuses,
    };
}